import React, { useMemo, useState } from 'react'
import { AlertType, bgLineGridColorDark, bgLineGridColorLight, corBgGrid, DadosBordero, DadosImpressaoBordero } from '../../Utils/Types'
import { createTheme, ThemeProvider } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectUser } from '../Redux/userSlice'
import { MRT_ColumnDef, MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import moment from 'moment'
import { Funcoes } from '../../Utils/Funcoes'
import { IoReceipt } from 'react-icons/io5'
import { Tooltip } from 'react-tooltip'
import { ReportReciboContaReceberBobina } from '../../Pages/ContasAReceber/pdf/report-reciboDupReceberBobina'
import { ContaAReceberService } from '../../Pages/ContasAReceber/ContaAReceberService'
import { ModalLoading } from '../Modal/modalLoading'
import { FeedbackMessage } from '../FeedbackMessage/feedback-message'

type Props = {
    duplicatas: DadosBordero[],
}

export type TipoStatus = | "A" | "L"

interface RowData {
    valor: number,
    id_bordero: number,
    name_user: string,
    name_cliente: string
    dt_baixa: string,
    obs_baixa: string
}

export const GridRelRecibos = React.memo(({ duplicatas }: Props) => {

    const [dadosParaImpressao, setDadosParaImpressao] = useState<DadosImpressaoBordero>()
    const [visibleImpressDupReceberBobina, setVisibleImpressDupReceberBobina] = useState(false)

    const [visibleLoading, setVisibleLoading] = useState(false)
    const [visible, setVisible] = useState(false)
    const [message, setMessage] = useState("")
    const [messageType, setMessageType] = useState<AlertType>("SUCCESS")

    let user = useSelector(state => selectUser(state))

    const fecharModalBobina = () => {
        setVisibleLoading(false)
        setVisibleImpressDupReceberBobina(false)
    }

    const minimizarFiltro = () => {
        let menuFiltro = document.getElementById("divMenuFiltroRecibos") as HTMLDivElement

        let menuInputs = document.getElementById("divInputsRecibos") as HTMLDivElement

        if (menuFiltro) {

            menuFiltro.classList.remove("max-h-[1000px]")
            menuFiltro.classList.add("max-h-[0px]")

            //setSearchBoxResponsivo(false)

            menuInputs.classList.add("invisible")
        }
    }

    const handleImprimir = async (id_bordero: number) => {

        setVisibleLoading(true)
        setVisible(false)

        try {

            const retApi = await ContaAReceberService.ImprimirBordero(id_bordero)

            if(retApi.error){

                setVisible(true)
                setMessageType("DANGER")
                setMessage(retApi.msg || '')
                setVisibleLoading(false)

            }else {
                
                setDadosParaImpressao(retApi.data)
                setVisibleImpressDupReceberBobina(true)

                if (minimizarFiltro) {
                    minimizarFiltro()
                }
            }  

        } catch (error) {
            setVisible(true)
            setMessageType("DANGER")
            setMessage("Houve um erro na impressão do recibo.")
            setVisibleLoading(false)
        } 

    }

    const handleCloseMessage = () => {
        setVisible(false)
    }

    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- TEMAS --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const theme = createTheme({
        palette: {
            mode: user.modeDark,
            background: {
                default: user.modeDark === 'light' ? '#fff' : '#475569',
            },
        },
    })

    const baseBackgroundColor = user.modeDark === 'dark' ? '#475569' : '#fff'


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- COLUNAS ------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const columns = useMemo<MRT_ColumnDef<DadosBordero>[]>(() => [

        {
            accessorKey: 'acao', header: 'Ações', visibleInShowHideMenu: true, size: 100, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData
                const handleIprimirClick = () => {

                    handleImprimir(rowData.id_bordero)
                    minimizarFiltro()
                }

                return (

                    <>
                        <div className='flex justify-start w-full'>

                            <button data-tooltip-id={'tooltip-recibo-' + rowData.id_bordero} id={'btn-recibo-' + rowData.id_bordero}
                                onClick={handleIprimirClick}
                                className={`flex items-center justify-center 
                                            text-white transition-all duration-300 
                                            hover:bg-sky-600 w-[30px] h-[30px] rounded-md 
                                            bg-sky-500`}>
                                <IoReceipt size="20px" />
                                <Tooltip style={{ backgroundColor: 'rgb(14, 165, 233)' }} id={'tooltip-recibo-' + rowData.id_bordero} anchorSelect={'#btn-recibo-' + rowData.id_bordero} positionStrategy="fixed" place='top-start'  >
                                    <span className={`font-sans font-medium text-[13px]`}>Imprimir</span>
                                </Tooltip>
                            </button>

                        </div>
                    </>

                )
            }
        },

        {
            accessorKey: 'name_cliente', visibleInShowHideMenu: true, header: 'Nome do Cliente', size: 300, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.name_cliente ? rowData.name_cliente : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'name_user', visibleInShowHideMenu: true, header: 'Usuário da Baixa', size: 300, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.name_user ? rowData.name_user : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'obs_baixa', visibleInShowHideMenu: true, header: 'Obs da Baixa', size: 300, muiTableBodyCellProps: { align: 'left' }, muiTableHeadCellProps: { align: 'left' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.obs_baixa ? rowData.obs_baixa : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'dt_baixa', header: 'Data da Baixa', visibleInShowHideMenu: true, size: 50, muiTableBodyCellProps: { align: 'center' }, muiTableHeadCellProps: { align: 'center' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.dt_baixa ? moment(rowData.dt_baixa).format('DD/MM/YYYY') : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

        {
            accessorKey: 'valor', header: 'Valor', visibleInShowHideMenu: true, size: 150, muiTableBodyCellProps: { align: 'right' }, muiTableHeadCellProps: { align: 'right' },

            Cell: (cell: any) => {

                const rowData = cell.row.original as RowData

                return (<span>{rowData.valor ? Funcoes.numberFormat(rowData.valor, 'currency') : <span className='text-red-400'>N/D</span>}</span>)

            }
        },

    ], []
    )


    //---------------------------------------------------------------------------------------------------------------------------------
    // ----------------------------------------------------------- TABLE --------------------------------------------------------------
    //---------------------------------------------------------------------------------------------------------------------------------
    const table = useMaterialReactTable({
        paginationDisplayMode: 'pages',
        columns: columns,
        data: duplicatas,
        autoResetPageIndex: false,
        mrtTheme: (theme) => ({
            baseBackgroundColor: baseBackgroundColor,
        }),
        muiTableHeadCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`,
                backgroundColor: corBgGrid,
                color: `#fff`,
                '& .MuiTableSortLabel-root': {
                    color: '#fff !important',
                    '&:hover': {
                        color: '#fff !important',
                    },
                    '&.Mui-active': {
                        color: '#fff !important',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                    '&.Mui-active .MuiTableSortLabel-icon': {
                        color: '#fff !important',
                    },
                },
                '& .MuiIconButton-root': {
                    color: '#fff',
                    '&:hover': {
                        color: '#fff',
                    },
                },
            },
        },
        muiTableBodyCellProps: {
            sx: {
                borderBottom: `0.1px solid ${user.modeDark === 'light' ? bgLineGridColorLight : bgLineGridColorDark}`
            }
        },
        localization: MRT_Localization_PT_BR,
        enableEditing: false,
        enableTopToolbar: false,
        enableColumnActions: false,
        enableColumnFilters: false,
        enableHiding: false,
        enableFilters: false,
        enableMultiSort: false,
        enableSorting: false,
        enableFullScreenToggle: false,
        enableDensityToggle: false,
        initialState: {
            pagination: { pageIndex: 0, pageSize: 10 },  // Define 10 elementos por página
        },
        muiPaginationProps: {
            rowsPerPageOptions: [10],  // Apenas uma opção para o seletor: 10
        },
    })

    return (
        <>

            <FeedbackMessage visible={visible} message={message} type={messageType} onCloseMessage={handleCloseMessage} />

            <ThemeProvider theme={theme}>
                <MaterialReactTable table={table} />
            </ThemeProvider>

            <ReportReciboContaReceberBobina
                visible={visibleImpressDupReceberBobina}
                dadosReimpressao={dadosParaImpressao}
                closeModalEvent={fecharModalBobina}
            />

            <ModalLoading visible={visibleLoading} />

        </>
    )

})